import React from 'react';
import { motion } from 'framer-motion';
import { Gauge, ScrollText, Compass, Layers, ShieldCheck, TestTube2 } from 'lucide-react';
interface Capability {
  icon: React.ElementType;
  title: string;
  description: string;
}
const capabilities: Capability[] = [{
  icon: Gauge,
  title: 'Code Complexity Analysis & Reporting',
  description: 'Automatically scan your legacy codebase and generate a scored complexity report covering code volume, logic depth, and dependencies. Know the true shape of your migration before committing to a plan.'
}, {
  icon: ScrollText,
  title: 'Automated Specification Recovery from Legacy Code',
  description: 'Extract and document the business rules, data flows, and functional behaviour buried in your legacy code. Turns decades of undocumented logic into structured specifications your team can actually work from.'
}, {
  icon: Compass,
  title: 'Migration Strategy Advisor',
  description: 'An interactive brainstorming wizard that helps you think through your migration approach, questioning assumptions, surfacing risks, and guiding you toward a phased plan tailored to your codebase and constraints.'
}, {
  icon: Layers,
  title: 'Stage-Based Migration & Tracking',
  description: 'Run migrations in controlled, reviewable waves rather than big-bang rewrites. Each stage has defined inputs, outputs, and acceptance criteria. Track progress at application and module level across your entire team.'
}, {
  icon: ShieldCheck,
  title: 'Code Review & Security Scanning',
  description: 'Every converted module is automatically scanned for vulnerabilities, compliance flags, and code quality issues before it reaches your team for human review. Catch what AI conversion misses before it reaches production.'
}, {
  icon: TestTube2,
  title: 'Automated Test Script Generation',
  description: 'Generate unit, integration, and regression test coverage directly from your migrated codebase. Verify functional equivalence between legacy and modern code with confidence, not guesswork.'
}];
export function PlatformCapabilities() {
  return <section id="capabilities" className="relative py-24 bg-brand-navy text-white overflow-hidden" data-id="element-201">
      {/* Blended background image — circuit/tech atmosphere */}
      <img src="https://images.unsplash.com/photo-1550063873-ab792950096b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1800&q=80" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-screen pointer-events-none select-none" data-id="element-202" />
      {/* Soft radial overlay to keep text legible while letting the image show */}
      <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-transparent to-brand-navy/60 pointer-events-none" data-id="element-203" />

      {/* Subtle dot grid background */}
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)',
      backgroundSize: '28px 28px'
    }} data-id="element-204" />
      {/* Soft accent glows */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-205">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-brand-green/10 blur-3xl" data-id="element-206" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-brand-blue/15 blur-3xl" data-id="element-207" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-208">
        {/* Centered editorial header */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl mx-auto text-center mb-16" data-id="element-209">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-brand-green text-xs font-semibold uppercase tracking-wider mb-5 backdrop-blur" data-id="element-210">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-green" data-id="element-211" />
            Platform Capabilities
          </span>
          <h2 className="text-3xl md:text-4xl font-bold font-heading leading-tight mb-5" data-id="element-212">
            A Complete Platform for{' '}
            <span className="text-brand-green" data-id="element-213">Legacy Modernization</span>
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed" data-id="element-214">
            Purpose-built capabilities that take you from understanding your
            codebase to a fully migrated, validated, and tested system.
          </p>
        </motion.div>

        {/* Capability grid — 3 cols, peer-level cards on navy */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-id="element-215">
          {capabilities.map((cap, index) => {
          const Icon = cap.icon;
          return <motion.article key={cap.title} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.45,
            delay: index % 3 * 0.08
          }} className="group relative rounded-2xl bg-white/[0.04] backdrop-blur border border-white/10 p-7 lg:p-8 hover:bg-white/[0.06] hover:border-brand-green/30 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden" data-id="element-216">
                {/* Corner accent glow on hover */}
                <span className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-brand-green/15 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-217" />

                <div className="relative" data-id="element-218">
                  <div className="w-12 h-12 rounded-xl bg-brand-green/15 border border-brand-green/20 flex items-center justify-center mb-6 group-hover:bg-brand-green group-hover:border-brand-green transition-colors duration-300" data-id="element-219">
                    <Icon className="w-5 h-5 text-brand-green group-hover:text-white transition-colors duration-300" strokeWidth={2.2} data-id="element-220" />
                  </div>

                  <h3 className="text-lg lg:text-xl font-bold text-white font-heading mb-3 leading-snug" data-id="element-221">
                    {cap.title}
                  </h3>

                  <p className="text-sm text-slate-300 leading-relaxed" data-id="element-222">
                    {cap.description}
                  </p>
                </div>
              </motion.article>;
        })}
        </div>
      </div>
    </section>;
}